import { IntegrationsSetting } from '@goldcast/api/backend';

import {
  TargetFields,
  CrmObjectType,
  FieldMappings,
  FieldMappingRowErrors,
  PrevFieldMappings,
  FieldMappingBooleans,
  ParsedIntegrationApiError
} from '@/studio/orgIntegration/store/types';

export const needANewTrayToken = trayUserToken => {
  if (!trayUserToken?.token) {
    return true;
  }

  const now = new Date().getTime();
  if (now - trayUserToken.tokenCreatedAt >= 24 * 60 * 60 * 1000) {
    return true;
  }

  return false;
};

export const areThereMissingAuthentications = (authsRequired, attachedAuths, configValues) => {
  let missingAuths: string[] = [];
  if (authsRequired[0] && authsRequired[0] === 'slack_plus_salesforce') {
    if (!attachedAuths.includes('slack')) {
      missingAuths.push('slack');
    }
    if (!attachedAuths.includes('salesforce')) {
      missingAuths.push('salesforce');
    }
  } else if (authsRequired[0] && authsRequired[0] === 'wistia') {
    const isConfValueSet = configValues && configValues[0] && configValues[0].value;
    if (!isConfValueSet || !configValues[0].value.trim()) {
      missingAuths.push('wistia');
    }
  } else {
    missingAuths = authsRequired.filter(rA => !attachedAuths.includes(rA));
  }
  return missingAuths;
};

export const getSegmentAnalyticsPayload = (context, instanceData) => {
  const currentOrg = context.rootGetters['org/getCurrentOrg'];
  const currentInstance = context.state.currentSolutionInstance;

  return {
    ordId: currentOrg.id,
    orgName: currentOrg.name,
    ...('solutionId' in instanceData
      ? { solutionId: instanceData.solutionId }
      : { solutionId: currentInstance.solutionId }),
    ...('solutionName' in instanceData
      ? { solutionName: instanceData.solutionName }
      : { solutionName: currentInstance.solutionTitle }),
    ...('solutionInstanceId' in instanceData
      ? { solutionInstanceId: instanceData.solutionInstanceId }
      : { solutionInstanceId: currentInstance.id }),
    ...('solutionInstanceName' in instanceData
      ? { solutionInstanceName: instanceData.solutionInstanceName }
      : { solutionInstanceName: currentInstance.name }),
    action: instanceData.action,
    modified: new Date().toISOString()
  };
};

export const parseFieldMappingRequestBody = fieldMappings =>
  fieldMappings.map(fieldMapping => ({
    goldcast_field_id: fieldMapping.goldcast_field_id,
    crm_field_id: fieldMapping.crm_field_id,
    is_required_crm_field: fieldMapping.is_required_crm_field,
    crm_field_type: fieldMapping.crm_field_type,
    crm_field_update_policy: fieldMapping.crm_field_update_policy
  }));

export const parseErrorFromServer = error => {
  try {
    const { non_field_errors: errorArray } = JSON.parse(error);

    if (errorArray[0]?.code === 'FIELDS_MAPPED_TO_SAME_CRM_FIELD' || errorArray[0]?.code === 'INVALID_UPDATE_POLICY') {
      return errorArray[0].message;
    }

    const parsedErrorArray = errorArray.map(err => (err.non_field_errors ? err.non_field_errors[0] : null));
    return parsedErrorArray;
  } catch {
    return null;
  }
};

export const getTargetFieldsDefaultState = (crmObjectTypes: CrmObjectType[]): TargetFields => {
  const targetFieldsDefaultState = {};
  crmObjectTypes.forEach(crmObjectType => {
    targetFieldsDefaultState[crmObjectType.crm_object_type] = {
      fields: [],
      isLoading: false,
      error: null
    };
  });
  return targetFieldsDefaultState;
};

export const getFieldMappingsDefaultState = (crmObjectTypes: CrmObjectType[]): FieldMappings => {
  const fieldMappingsDefaultState = {};
  crmObjectTypes.forEach(crmObjectType => {
    fieldMappingsDefaultState[crmObjectType.crm_object_type] = {
      mappings: [],
      isLoading: false
    };
  });
  return fieldMappingsDefaultState;
};

export const getPrevFieldMappingsDefaultState = (crmObjectTypes: CrmObjectType[]): PrevFieldMappings => {
  const fieldMappingsDefaultState = {};
  crmObjectTypes.forEach(crmObjectType => {
    fieldMappingsDefaultState[crmObjectType.crm_object_type] = [];
  });
  return fieldMappingsDefaultState;
};

export const getSaveFieldMappingLoadingDefaultState = (crmObjectTypes: CrmObjectType[]): FieldMappingBooleans => {
  const isSaveFieldMappingLoading = {};
  crmObjectTypes.forEach(crmObjectType => {
    isSaveFieldMappingLoading[crmObjectType.crm_object_type] = false;
  });
  return isSaveFieldMappingLoading;
};

export const getFieldMappingRowErrorDefaultState = (crmObjectTypes: CrmObjectType[]): FieldMappingRowErrors => {
  const fieldMappingRowErrorDefaultState = {};
  crmObjectTypes.forEach(crmObjectType => {
    fieldMappingRowErrorDefaultState[crmObjectType.crm_object_type] = [];
  });
  return fieldMappingRowErrorDefaultState;
};

export const parseIntegrationApiError = (errorObj): ParsedIntegrationApiError => {
  const parsedError = { message: 'Something went wrong! Try again.' };
  try {
    const { error } = JSON.parse(errorObj.message);
    if (error && error.message) {
      parsedError.message = error.message;
    }
    return parsedError;
  } catch {
    return parsedError;
  }
};

export function getMarketoCustomActivityErrorMessage(
  integrationSettings: IntegrationsSetting[],
  currentSolutionInstanceId: string
) {
  const integrationSetting = integrationSettings?.find(setting => setting.entity_id === currentSolutionInstanceId);
  const isUserProfileActivityCreated = Boolean(integrationSetting?.marketo_user_profile_id);
  const isUserEngagementActivityCreated = Boolean(integrationSetting?.marketo_user_engagement_id);
  if (!isUserProfileActivityCreated && !isUserEngagementActivityCreated) {
    return `Failed to create new Custom Activities: "User Profile" and "User Engagement"`;
  }
  if (!isUserProfileActivityCreated) {
    return `Failed to create a new Custom Activity: "User Profile"`;
  }
  if (!isUserEngagementActivityCreated) {
    return `Failed to create a new Custom Activity: "User Engagement"`;
  }
  return '';
}

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
